import dayjs from "dayjs"
import Cookies from "js-cookie"
import { parseJwt } from "utils/api"
import {
  clearAuthDataLocalStorage,
  getAuthDataLocalStorage,
} from "utils/authData"

export const getAuthInitialData = () => {
  const authData = getAuthDataLocalStorage()
  if (!authData || !authData.token) {
    clearAuthDataLocalStorage()
    return null
  }

  try {
    const tokenParsedToken = parseJwt(authData.token) as {
      exp: number
      sub: string
    }

    const tokenExpireTime = new Date(tokenParsedToken.exp * 1000)
    const currentDatePlusFiveDays = dayjs().add(5, "day").toDate()

    if (!dayjs(currentDatePlusFiveDays).isBefore(tokenExpireTime)) {
      clearAuthDataLocalStorage()
      window.location.href = "/"
      return null
    }
    return authData
  } catch {
    clearAuthDataLocalStorage()
    return null
  }
}

// Mirrors the escaped-string decoding in CleverSSOLoader / OpenIdSSOLoader.
// That duplication is intentional: those loaders are left untouched (surgical
// change). If the cookie format changes, update all three.
const parseAuthDataCookie = (): { token?: string } | null => {
  const raw = Cookies.get("auth_data")
  if (!raw) return null

  try {
    const decoded = raw
      .replace(/\\"/g, "\"") // \" -> "
      .replace(/\\054/g, ",") // \054 -> , (ASCII code for comma)
    const parsed: unknown = JSON.parse(decoded)

    if (
      parsed &&
      typeof parsed === "object" &&
      "token" in parsed &&
      typeof (parsed as { token?: unknown }).token === "string"
    ) {
      return { token: (parsed as { token: string }).token }
    }

    return null
  } catch {
    return null
  }
}

const getJwtSub = (token: string): string | null => {
  try {
    const sub = (parseJwt(token) as { sub?: unknown }).sub
    return typeof sub === "string" && sub.length > 0 ? sub : null
  } catch {
    return null
  }
}

/**
 * Handles the shared-device case where person A still has a live local session
 * and person B returns from a provider SSO redirect carrying their own JWT in
 * the `auth_data` cookie (Clever / TeachHub / ClassLink — all three use the
 * same cookie).
 *
 * Without this, `<TeacherRoutes/>` renders for A, the SSO loader never mounts,
 * the cookie is never consumed and B lands on A's dashboard.
 *
 * Called once from application bootstrap (`bootstrap/reconcileSso.ts`), imported
 * before the Redux store's `initialState` reads `localStorage` — so on the same
 * load the store initialises with no auth data, `<UnauthRoutes/>` renders and
 * the normal loader consumes the still-present `auth_data` cookie from a clean
 * slate. No reload needed. No loop: `localStorage` is now empty.
 *
 * Returns `true` when a stale session was cleared. Analytics teardown for the
 * cleared session (`mixpanel.reset()`) is left to `App` — it runs at bootstrap,
 * before `mixpanel.init()`, so calling it here throws.
 */
export const reconcileIncomingSsoSession = (): boolean => {
  const oldToken = getAuthDataLocalStorage()?.token
  if (!oldToken) return false

  const cookieToken = parseAuthDataCookie()?.token
  if (!cookieToken) return false

  const cookieSub = getJwtSub(cookieToken)
  if (!cookieSub) return false

  const oldSub = getJwtSub(oldToken)

  // Different person, or a broken local session paired with a fresh cookie.
  if (oldSub === cookieSub) return false

  clearAuthDataLocalStorage()
  if (window.Intercom) window.Intercom("shutdown")
  return true
}
